import {DataTableColumn} from 'mantine-datatable';
import {NavLink} from 'react-router-dom';
import {I18n} from 'aws-amplify/utils';
import {
    Badge,
    Text,
} from '@mantine/core';

import {HostedZone} from '../types';
import config from '../config';

// Route 53 returns a zone name with a TRAILING DOT — "test.flexi.network." — and
// that is the fully-qualified form, not a formatting artefact. It is shown as
// returned rather than trimmed: an operator comparing this against the AWS
// console or a dig output should see the same string.

export const useHostedZonesTableColumns = () => {

    const columns: DataTableColumn<HostedZone>[] = [
        {
            accessor: 'name',
            sortable: true,
            title: I18n.get('Name'),
            // FNP-1284 - NO WIDTH ON THE FLEXIBLE COLUMN. Leave it that way.
            //
            // A column with no width takes the space the fixed columns leave. A grid
            // fraction here made it content-size instead: the fixed columns are
            // committed first and this one grew to fit its longest value, pushing the
            // table past its container and raising a horizontal scrollbar.
            //
            // `minWidth` in px is fine if a floor is ever wanted. A grid fraction is
            // not: mantine-datatable puts the value in an inline style on a real
            // <table> cell, where `fr` is not a valid unit.
            render: (item: HostedZone) => (
                <NavLink to={`${config.basePath}/${encodeURIComponent(item.id)}`}>
                    <Text size={'sm'}>{item.name}</Text>
                </NavLink>
            ),
        },
        {
            accessor: 'recordSetCount',
            sortable: true,
            title: I18n.get('Record Sets'),
            // Compares NUMERICALLY via the shared comparator — an Int sorted as a
            // string puts "10" before "9".
            render: (item: HostedZone) => (
                <Text size={'sm'}>
                    {typeof item.recordSetCount === 'number' ? item.recordSetCount : '—'}
                </Text>
            ),
        },
        {
            accessor: 'privateZone',
            sortable: true,
            title: I18n.get('Visibility'),
            render: (item: HostedZone) => {
                // Null is a distinct state from false — the API did not say. An
                // em dash rather than defaulting it to "Public", which would be a
                // claim the response never made.
                if (typeof item.privateZone !== 'boolean') {
                    return <Text size={'sm'} c={'dimmed'}>—</Text>;
                }
                return (
                    <Badge
                        variant={'light'}
                        color={item.privateZone ? 'orange' : 'blue'}
                    >
                        {item.privateZone ? I18n.get('Private') : I18n.get('Public')}
                    </Badge>
                );
            },
        },
        {
            accessor: 'accountId',
            sortable: true,
            title: I18n.get('Account'),
            render: (item: HostedZone) => (
                <Text size={'xs'} ff={'monospace'} c={'dimmed'}>{item.accountId}</Text>
            ),
        },
        {
            accessor: 'id',
            sortable: true,
            title: I18n.get('Zone ID'),
            render: (item: HostedZone) => (
                <Text size={'xs'} ff={'monospace'} c={'dimmed'}>{item.id}</Text>
            ),
        },
    ];

    return {
        columns,
    };
};
